import { DateTime } from "luxon";

import { populateEmptyTimesheetEntries } from "../../hooks";
import type { TimeCard, TimesheetEntry } from "../../utils";

/**
 * Gets the index of the start time (first PUNCH_IN) in a timecard's entries
 * ASSUMPTION: Entries are sorted chronologically
 */
export function getTimeCardEntriesStartTimeIndex(entries: TimeCard["entries"]): number | undefined {
  if (entries.length === 0) return undefined;
  if (entries[0].type !== "PUNCH_IN") return undefined;
  return 0;
}

/**
 * Gets the index of the end time (last PUNCH_OUT) in a timecard's entries
 * ASSUMPTION: Entries are sorted chronologically
 */
export function getTimeCardEntriesEndTimeIndex(entries: TimeCard["entries"]): number | undefined {
  if (entries.length === 0) return undefined;
  if (entries[entries.length - 1].type !== "PUNCH_OUT") return undefined;
  return entries.length - 1;
}

/**
 * Given a TimeCard, return the start and end time for the meal break.
 *
 * ASSUMPTION: The TimeCard entries are sorted by timestamp.
 */
export function getTimeCardMealBreakIndices(params: {
  timeCard: TimeCard;
  mealBreakIndex: 1 | 2;
}): {
  startTimeIndex?: number;
  endTimeIndex?: number;
} {
  const { timeCard, mealBreakIndex } = params;

  /*** Constants ***/
  const punchInEntriesIndices = timeCard.entries
    .map((entry, index) => (entry.type === "PUNCH_IN" ? index : undefined))
    .filter((index) => index !== undefined);
  const punchOutEntriesIndices = timeCard.entries
    .map((entry, index) => (entry.type === "PUNCH_OUT" ? index : undefined))
    .filter((index) => index !== undefined);

  // Find the meal break start
  const startTimeIndex =
    // There must be more than 1 punch out entry to have a meal break start time
    punchOutEntriesIndices.length > mealBreakIndex
      ? punchOutEntriesIndices.at(mealBreakIndex - 1)
      : undefined;
  // Find the meal break end
  const endTimeIndex =
    // There must be more than 1 punch in entry to have a meal break end time
    punchInEntriesIndices.length > mealBreakIndex
      ? punchInEntriesIndices.at(mealBreakIndex)
      : undefined;

  return { startTimeIndex, endTimeIndex };
}

/**
 * Returns default times for time picker initial values by using non-CA logic.
 * This ensures CA users still get sensible initial values when opening
 * the time picker, even though their actual values are undefined.
 */
export function getTimeCardEntriesDefaultTimes(params: { timeCard: TimeCard; now: DateTime }): {
  defaultStartTime: DateTime;
  defaultEndTime: DateTime;
  defaultMealBreak1StartTime: DateTime;
  defaultMealBreak1EndTime: DateTime;
} {
  const { timeCard, now } = params;
  const timeCardWithDefaults = populateEmptyTimesheetEntries({
    timesheetEntries: [timeCard as TimesheetEntry],
    now,
  })[0] as TimeCard;

  /*** Get Start and End DateTimes ***/
  // NOTE: Using `!` here because we know that these entries will be guaranteed
  // to exist based on the logic of the `populateEmptyTimesheetEntries`
  // function. TLDR: Each TimeCard will have at one PUNCH_IN representing the
  // start time of the request, and one PUNCH_OUT representing the end time of
  // the request.
  const startTimeIndex = getTimeCardEntriesStartTimeIndex(timeCardWithDefaults.entries)!;
  const defaultStartTime = timeCardWithDefaults.entries[startTimeIndex].ts!;

  const endTimeIndex = getTimeCardEntriesEndTimeIndex(timeCardWithDefaults.entries)!;
  const defaultEndTime = timeCardWithDefaults.entries[endTimeIndex].ts!;

  /*** Get Meal Break DateTimes ***/
  // When getting the meal break indices, we can't guarantee that the meal break
  // there will be any due to a Request being less than 5 hours long will not
  // by default have one. Thus here we will handle by creating a meal break
  // for the entire duration of the request.
  const { startTimeIndex: mealBreak1StartIndex, endTimeIndex: mealBreak1EndIndex } =
    getTimeCardMealBreakIndices({
      timeCard: timeCardWithDefaults,
      mealBreakIndex: 1,
    });

  const defaultMealBreak1StartTime =
    timeCardWithDefaults.entries[mealBreak1StartIndex ?? -1]?.ts ??
    defaultEndTime.minus({ minutes: 30 });
  const defaultMealBreak1EndTime =
    timeCardWithDefaults.entries[mealBreak1EndIndex ?? -1]?.ts ?? defaultEndTime;

  return {
    defaultStartTime,
    defaultEndTime,
    defaultMealBreak1StartTime,
    defaultMealBreak1EndTime,
  };
}
